// Reports Page for AAR Guest Management System
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, onSnapshot, Timestamp, documentId } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessReports } from '../utils/roles';
import { formatFullDateTime, formatDuration, calculateDuration } from '../utils/dateUtils';
import { fetchParticipants } from '../firebase/firestoreService';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/Reports.css';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [timeframe, setTimeframe] = useState('week');
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [guests, setGuests] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expandedCards, setExpandedCards] = useState({});
  const [tileFilter, setTileFilter] = useState(null); // null | 'active' | 'missing'
  const [allUncheckedGuests, setAllUncheckedGuests] = useState([]);
  const navigate = useNavigate();
  const user = getCurrentUser();

  // Subscribe to all currently checked-in guests (all-time, ignoring the report
  // timeframe) so the "Unchecked Guests" tile always reflects the true backlog.
  useEffect(() => {
    const checkInsRef = collection(db, 'checkIns');
    const q = query(checkInsRef, where('status', '==', 'checked_in'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const guestMap = new Map();
      snapshot.forEach(docSnap => {
        const c = docSnap.data();
        if (c.guestDeleted) return;
        if (c.serviceType === 'note') return;
        const key = `${c.guestFirstName} ${c.guestLastName}`;
        if (!guestMap.has(key)) {
          guestMap.set(key, {
            name: key,
            firstName: c.guestFirstName,
            lastName: c.guestLastName,
            image: null,
            phone: '',
            email: '',
            emergencyContactName: '',
            emergencyContactPhone: '',
            visits: [],
            totalVisits: 0,
            totalDuration: 0,
          });
        }
        const g = guestMap.get(key);
        g.visits.push({
          id: docSnap.id,
          checkInTime: c.checkInTime,
          checkOutTime: c.checkOutTime,
          serviceType: c.serviceType,
          status: c.status,
          duration: 0,
          pagerNumber: c.pagerNumber,
          pagerRetrieved: c.pagerRetrieved === true,
          notes: '',
        });
        g.totalVisits++;
      });
      const list = Array.from(guestMap.values()).sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      setAllUncheckedGuests(list);
    }, (err) => {
      console.error('Error loading all-time unchecked guests:', err);
    });
    return () => unsubscribe();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Format phone number to (XXX) XXX-XXXX
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length > 10) {
      // Handle longer numbers by taking last 10 digits
      return `(${cleaned.slice(-10, -7)}) ${cleaned.slice(-7, -4)}-${cleaned.slice(-4)}`;
    }

    return phone;
  };

  // Format service type for display
  const formatServiceType = (serviceType) => {
    if (serviceType === 'tuesday_prayer') {
      return 'TPM';
    }
    return serviceType;
  };

  const toggleCardExpanded = (index) => {
    setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Format short date for card display
  const formatShortDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ', ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Convert "HH:MM" 24h string to "h:MM AM/PM"
  const formatTime12h = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    const h = parseInt(parts[0], 10);
    const mm = parts[1];
    if (isNaN(h)) return timeStr;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${mm} ${period}`;
  };

  // Build a compact "Pickup / Vehicle" cell from visit data
  const formatPickupVehicle = (visit) => {
    const parts = [];
    if (visit.actualPickupTime) parts.push(formatTime12h(visit.actualPickupTime));
    if (visit.vidNumber) parts.push(`VID ${visit.vidNumber}`);
    if (visit.licPlateNumber) parts.push(`Plate ${visit.licPlateNumber}`);
    if (visit.vehicleColor) parts.push(visit.vehicleColor);
    return parts.join(' • ');
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    getUserRole(user.uid).then(role => {
      if (!canAccessReports(role)) {
        navigate('/dashboard');
      } else {
        loadGuests();
      }
    });
  }, [user, navigate]);

  // Generate report on load and when timeframe changes
  useEffect(() => {
    if (guests.length > 0 && !generating) {
      if (selectedGuest) {
        generateReportForGuest(selectedGuest);
      } else {
        generateReportForAllGuests();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests, timeframe]);

  const generateReportForAllGuests = async () => {
    try {
      setGenerating(true);
      setError('');

      const { start, end } = getTimeframeRange(timeframe);

      const checkInsRef = collection(db, 'checkIns');
      const q = query(
        checkInsRef,
        where('checkInTime', '>=', Timestamp.fromDate(start)),
        where('checkInTime', '<=', Timestamp.fromDate(end))
      );

      const querySnapshot = await getDocs(q);
      const guestMap = new Map();
      let sundayCheckIns = 0;
      let tuesdayCheckIns = 0;
      const serviceCheckIns = { '9am': 0, '12pm': 0, '3pm': 0, 'tuesday_prayer': 0 };

      querySnapshot.forEach((doc) => {
        const checkIn = { id: doc.id, ...doc.data() };

        // Skip check-ins for deleted guests
        if (checkIn.guestDeleted === true) {
          return;
        }

        // Skip notes
        if (checkIn.serviceType === 'note') {
          return;
        }

        // Count by service day and per-service id
        if (checkIn.serviceType === 'tuesday_prayer') {
          tuesdayCheckIns++;
        } else {
          sundayCheckIns++;
        }
        if (serviceCheckIns.hasOwnProperty(checkIn.serviceType)) {
          serviceCheckIns[checkIn.serviceType]++;
        }

        const guestKey = `${checkIn.guestFirstName} ${checkIn.guestLastName}`;

        if (!guestMap.has(guestKey)) {
          // Find guest info from participants list
          const guestInfo = guests.find(g =>
            g.firstName === checkIn.guestFirstName &&
            g.lastName === checkIn.guestLastName
          );

          guestMap.set(guestKey, {
            name: guestKey,
            firstName: checkIn.guestFirstName,
            lastName: checkIn.guestLastName,
            image: guestInfo?.image,
            phone: guestInfo?.phone || '',
            email: guestInfo?.email || '',
            visits: [],
            totalVisits: 0,
            totalDuration: 0
          });
        }

        const guest = guestMap.get(guestKey);

        let duration = 0;
        if (checkIn.status === 'checked_out' && checkIn.checkOutTime) {
          duration = calculateDuration(checkIn.checkInTime, checkIn.checkOutTime);
        }

        const allNotes = [
          checkIn.checkInNotes ? `[Check-In] ${checkIn.checkInNotes}` : '',
          checkIn.checkOutNotes ? `[Check-Out] ${checkIn.checkOutNotes}` : ''
        ].filter(Boolean).join(' | ');

        guest.visits.push({
          id: checkIn.id,
          checkInTime: checkIn.checkInTime,
          checkOutTime: checkIn.checkOutTime,
          serviceType: checkIn.serviceType,
          status: checkIn.status,
          duration: duration,
          pagerNumber: checkIn.pagerNumber,
          pagerRetrieved: checkIn.pagerRetrieved === true,
          notes: allNotes,
          expectedPickupTime: checkIn.expectedPickupTime || '',
          actualPickupTime: checkIn.actualPickupTime || '',
          vidNumber: checkIn.vidNumber || '',
          licPlateNumber: checkIn.licPlateNumber || '',
          vehicleColor: checkIn.vehicleColor || '',
          pickupNotes: checkIn.pickupNotes || ''
        });

        guest.totalVisits++;
        guest.totalDuration += duration;
      });

      // Fetch additional attendance for the date range
      const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
      let totalAdditionalSunday = 0;
      let totalAdditionalTuesday = 0;
      const serviceAdditional = {
        '9am': { '163_livingston': 0, 'smith_lobby': 0 },
        '12pm': { '163_livingston': 0, 'smith_lobby': 0 },
        '3pm': { '163_livingston': 0, 'smith_lobby': 0 },
        'tuesday_prayer': { '163_livingston': 0, 'smith_lobby': 0 }
      };
      try {
        const attendanceRef = collection(db, 'additionalAttendance');
        const attendanceQuery = query(
          attendanceRef,
          where(documentId(), '>=', startStr),
          where(documentId(), '<=', endStr)
        );
        const attendanceSnapshot = await getDocs(attendanceQuery);
        attendanceSnapshot.forEach(doc => {
          const data = doc.data();
          totalAdditionalSunday += (data.sundayCount || 0);
          totalAdditionalTuesday += (data.tuesdayCount || 0);
          if (data.counts && typeof data.counts === 'object') {
            Object.entries(data.counts).forEach(([serviceId, locs]) => {
              if (!serviceAdditional[serviceId] || !locs) return;
              Object.entries(locs).forEach(([locId, n]) => {
                if (serviceAdditional[serviceId].hasOwnProperty(locId)) {
                  serviceAdditional[serviceId][locId] += (Number(n) || 0);
                }
              });
            });
          } else {
            // Legacy doc — assume single bucket at 163 Livingston, 9am for Sunday
            if (data.sundayCount) {
              serviceAdditional['9am']['163_livingston'] += (data.sundayCount || 0);
            }
            if (data.tuesdayCount) {
              serviceAdditional['tuesday_prayer']['163_livingston'] += (data.tuesdayCount || 0);
            }
          }
        });
      } catch (err) {
        console.error('Error fetching additional attendance:', err);
      }

      const totalCheckIns = sundayCheckIns + tuesdayCheckIns;

      const guestReports = Array.from(guestMap.values()).sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setReportData({
        timeframe,
        startDate: start,
        endDate: end,
        totalGuests: guestReports.length,
        totalCheckIns,
        sundayCheckIns,
        tuesdayCheckIns,
        totalAdditionalSunday,
        totalAdditionalTuesday,
        totalSundayAttendance: sundayCheckIns + totalAdditionalSunday,
        totalPrayerMeetingAttendance: tuesdayCheckIns + totalAdditionalTuesday,
        totalAttendance: totalCheckIns + totalAdditionalSunday + totalAdditionalTuesday,
        serviceCheckIns,
        serviceAdditional,
        guests: guestReports
      });

    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const generateReportForGuest = async (guest) => {
    try {
      setGenerating(true);
      setError('');

      const { start, end } = getTimeframeRange(timeframe);

      const checkInsRef = collection(db, 'checkIns');
      const q = query(
        checkInsRef,
        where('checkInTime', '>=', Timestamp.fromDate(start)),
        where('checkInTime', '<=', Timestamp.fromDate(end))
      );

      const querySnapshot = await getDocs(q);
      const checkIns = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Skip check-ins for deleted guests
        if (data.guestDeleted === true) {
          return;
        }
        if (data.guestFirstName === guest.firstName &&
            data.guestLastName === guest.lastName) {
          checkIns.push({ id: doc.id, ...data });
        }
      });

      const guestData = {
        name: `${guest.firstName} ${guest.lastName}`,
        firstName: guest.firstName,
        lastName: guest.lastName,
        image: guest.image,
        phone: guest.phone || '',
        email: guest.email || '',
        emergencyContactName: guest.emergencyContactName || '',
        emergencyContactPhone: guest.emergencyContactPhone || '',
        visits: [],
        totalVisits: 0,
        totalDuration: 0
      };

      const guestMap = new Map();
      guestMap.set(guestData.name, guestData);

      checkIns.forEach(checkIn => {
        const guestKey = `${checkIn.guestFirstName} ${checkIn.guestLastName}`;

        if (!guestMap.has(guestKey)) {
          guestMap.set(guestKey, {
            name: guestKey,
            firstName: checkIn.guestFirstName,
            lastName: checkIn.guestLastName,
            visits: [],
            totalVisits: 0,
            totalDuration: 0
          });
        }

        const guestObj = guestMap.get(guestKey);

        let duration = 0;
        if (checkIn.status === 'checked_out' && checkIn.checkOutTime) {
          duration = calculateDuration(checkIn.checkInTime, checkIn.checkOutTime);
        }

        const allNotes2 = [
          checkIn.checkInNotes ? `[Check-In] ${checkIn.checkInNotes}` : '',
          checkIn.checkOutNotes ? `[Check-Out] ${checkIn.checkOutNotes}` : ''
        ].filter(Boolean).join(' | ');

        guestObj.visits.push({
          id: checkIn.id,
          checkInTime: checkIn.checkInTime,
          checkOutTime: checkIn.checkOutTime,
          serviceType: checkIn.serviceType,
          status: checkIn.status,
          duration: duration,
          pagerNumber: checkIn.pagerNumber,
          pagerRetrieved: checkIn.pagerRetrieved === true,
          notes: allNotes2,
          expectedPickupTime: checkIn.expectedPickupTime || '',
          actualPickupTime: checkIn.actualPickupTime || '',
          vidNumber: checkIn.vidNumber || '',
          licPlateNumber: checkIn.licPlateNumber || '',
          vehicleColor: checkIn.vehicleColor || '',
          pickupNotes: checkIn.pickupNotes || ''
        });

        guestObj.totalVisits++;
        guestObj.totalDuration += duration;
      });

      const guestReports = Array.from(guestMap.values()).sort((a, b) => b.totalVisits - a.totalVisits);

      setReportData({
        timeframe,
        startDate: start,
        endDate: end,
        totalGuests: guestReports.length,
        totalCheckIns: checkIns.length,
        guests: guestReports
      });

    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const loadGuests = async () => {
    try {
      const participantList = await fetchParticipants('1269');
      // Sort guests alphabetically
      const sortedGuests = participantList.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setGuests(sortedGuests);
      setFilteredGuests(sortedGuests);
    } catch (err) {
      console.error('Error loading guests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.length > 0) {
      const filtered = guests.filter(guest =>
        `${guest.firstName} ${guest.lastName}`.toLowerCase().includes(term.toLowerCase())
      ).sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setFilteredGuests(filtered);
      setShowDropdown(true);
    } else {
      // Show all guests in alphabetical order when search is empty
      const sortedGuests = [...guests].sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setFilteredGuests(sortedGuests);
      setSelectedGuest(null);
    }
  };

  const handleSearchClick = () => {
    // Show all guests in alphabetical order when clicking the search field
    const sortedGuests = [...guests].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    setFilteredGuests(sortedGuests);
    setShowDropdown(true);
  };

  const handleGuestSelect = async (guest) => {
    setSelectedGuest(guest);
    setSearchTerm(`${guest.firstName} ${guest.lastName}`);
    setShowDropdown(false);
    // Generate report for selected guest
    await generateReportForGuest(guest);
  };

  const getTimeframeRange = (timeframe) => {
    const now = new Date();
    const start = new Date();

    switch (timeframe) {
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'semi-annual':
        start.setMonth(now.getMonth() - 6);
        break;
      case 'annual':
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        start.setDate(now.getDate() - 7);
    }

    return { start, end: now };
  };

  const generateReport = async () => {
    if (!selectedGuest) {
      // If no guest selected, generate report for all guests
      await generateReportForAllGuests();
    } else {
      // Generate report for specific guest
      await generateReportForGuest(selectedGuest);
    }
  };

  const generateAllGuestsReport = async () => {
    // Clear any selected guest and search term
    setSelectedGuest(null);
    setSearchTerm('');
    setShowDropdown(false);
    // Generate report for all guests
    await generateReportForAllGuests();
  };

  const getTimeframeLabel = (tf) => {
    const labels = {
      week: 'Past Week',
      month: 'Past Month',
      quarter: 'Past Quarter (3 Months)',
      'semi-annual': 'Past 6 Months',
      annual: 'Past Year (12 Months)'
    };
    return labels[tf] || tf;
  };

  const exportToCSV = () => {
    if (!reportData || !reportData.guests.length) return;

    const headers = ['Guest Name', 'Phone', 'Email', 'Emergency Contact Name', 'Emergency Contact Phone', 'Check-In Time', 'Service Type', 'Check-Out Time', 'Duration', 'Status', 'Pager Number', 'Expected Pickup Time', 'Actual Pickup Time', 'VID #', 'LIC Plate #', 'Vehicle Color', 'Pickup Notes', 'Notes'];
    const rows = [];

    reportData.guests.forEach(guest => {
      guest.visits.forEach(visit => {
        rows.push([
          guest.name,
          guest.phone || '',
          guest.email || '',
          guest.emergencyContactName || '',
          guest.emergencyContactPhone || '',
          formatFullDateTime(visit.checkInTime),
          visit.serviceType,
          visit.checkOutTime ? formatFullDateTime(visit.checkOutTime) : 'N/A',
          visit.duration > 0 ? formatDuration(visit.duration) : 'N/A',
          visit.status === 'checked_out' ? 'Completed' : 'Active',
          visit.pagerNumber || 'N/A',
          visit.expectedPickupTime ? formatTime12h(visit.expectedPickupTime) : '',
          visit.actualPickupTime ? formatTime12h(visit.actualPickupTime) : '',
          visit.vidNumber || '',
          visit.licPlateNumber || '',
          visit.vehicleColor || '',
          visit.pickupNotes || '',
          visit.notes || ''
        ]);
      });
    });

    // Build attendance summary rows at the top
    const SERVICE_LABELS = {
      '9am': 'Sunday 9 AM',
      '12pm': 'Sunday 12 PM',
      '3pm': 'Sunday 3 PM',
      'tuesday_prayer': 'Tuesday Prayer'
    };
    const summaryRows = [
      `"Attendance Summary"`,
      `"Timeframe","${getTimeframeLabel(reportData.timeframe)}","${reportData.startDate.toLocaleDateString()} - ${reportData.endDate.toLocaleDateString()}"`,
      `"Total Attendance","${reportData.totalAttendance || reportData.totalCheckIns}"`,
      `"Total Sunday Attendance","${reportData.totalSundayAttendance || ''}","${reportData.sundayCheckIns || 0} checked in + ${reportData.totalAdditionalSunday || 0} additional"`,
      `"Total Prayer Meeting Attendance","${reportData.totalPrayerMeetingAttendance || ''}","${reportData.tuesdayCheckIns || 0} checked in + ${reportData.totalAdditionalTuesday || 0} additional"`,
      `"Unique Guests","${reportData.totalGuests}"`,
      `"Total Check-Ins","${reportData.totalCheckIns}"`,
      '',
    ];

    if (reportData.serviceCheckIns && reportData.serviceAdditional) {
      summaryRows.push(`"Service Breakdown"`);
      summaryRows.push(`"Service","Checked In","163 Livingston","Smith Lobby","Service Total"`);
      Object.entries(SERVICE_LABELS).forEach(([sid, label]) => {
        const ci = reportData.serviceCheckIns[sid] || 0;
        const livingston = reportData.serviceAdditional[sid]?.['163_livingston'] || 0;
        const smith = reportData.serviceAdditional[sid]?.['smith_lobby'] || 0;
        const total = ci + livingston + smith;
        if (total === 0) return;
        summaryRows.push(`"${label}","${ci}","${livingston}","${smith}","${total}"`);
      });
      summaryRows.push('');
    }

    const csvContent = [
      ...summaryRows,
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Get filename based on whether it's a single guest or all guests
    const isMultipleGuests = reportData.guests.length > 1;
    const filename = isMultipleGuests
      ? `all-guests-activity-report-${timeframe}-${new Date().toISOString().split('T')[0]}.csv`
      : `${reportData.guests[0]?.name.replace(/\s+/g, '-')}-activity-report-${timeframe}-${new Date().toISOString().split('T')[0]}.csv`;

    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (!reportData || !reportData.guests.length) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to generate PDF');
      return;
    }

    // Generate report for all guests or single guest
    const isMultipleGuests = reportData.guests.length > 1;
    const reportTitle = isMultipleGuests
      ? 'All Guests Activity Report'
      : `${reportData.guests[0].name} - Guest Activity Report`;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            margin: 20px;
            color: #333;
            background-color: #eef0f2;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #1a5f7a;
            padding-bottom: 10px;
          }
          .header h1 {
            color: #1a5f7a;
            margin: 0 0 5px 0;
            font-size: 22px;
          }
          .header p {
            margin: 0;
            font-size: 12px;
            color: #666;
          }
          /* Flat Card Layout */
          .flat-card {
            background-color: #ffffff;
            display: flex;
            width: 100%;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
            overflow: hidden;
            margin-bottom: 6px;
          }
          .card-image {
            width: 120px;
            max-width: 120px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 10px 0 10px 14px;
          }
          .card-image img {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            object-fit: cover;
            display: block;
          }
          .card-content {
            flex-grow: 1;
            padding: 12px 18px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            position: relative;
            min-height: 90px;
          }
          .card-top {
            font-size: 11px;
            color: #333;
            display: flex;
            gap: 16px;
            align-items: center;
            letter-spacing: 0.2px;
            font-weight: 600;
          }
          .green-dot {
            color: #2ba265;
            font-size: 14px;
            margin-right: 2px;
          }
          .card-corner {
            position: absolute;
            top: 10px;
            right: 18px;
            display: flex;
            gap: 5px;
            flex-wrap: wrap;
            justify-content: flex-end;
            max-width: 50%;
          }
          .service-count-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            background: #f0f4f8;
            color: #4a5568;
            font-size: 10px;
            font-weight: 600;
            padding: 2px 7px;
            border-radius: 3px;
            white-space: nowrap;
          }
          .service-count-badge .service-count {
            background: #1a5f7a;
            color: white;
            font-size: 9px;
            font-weight: 700;
            min-width: 14px;
            height: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 3px;
            padding: 0 3px;
          }
          .card-title {
            font-size: 15px;
            color: #333333;
            font-weight: 400;
            padding-left: 12px;
          }
          .card-bottom {
            display: flex;
            gap: 24px;
            align-items: center;
            padding-left: 12px;
          }
          .bottom-item {
            font-size: 9px;
            color: #8c929a;
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .bottom-item svg {
            width: 10px;
            height: 10px;
            stroke: #8c929a;
            stroke-width: 1.5;
            fill: none;
          }
          /* Visit History Table */
          .visits-section {
            border-top: 1px solid #eef0f2;
            padding: 8px 18px 12px;
            margin-bottom: 14px;
          }
          .visits-section h4 {
            margin: 0 0 6px 0;
            font-size: 9px;
            color: #8c929a;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th {
            color: #8c929a;
            padding: 5px 6px;
            text-align: left;
            font-size: 8px;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.4px;
            border-bottom: 1px solid #eef0f2;
          }
          td {
            padding: 6px;
            font-size: 9px;
            color: #4a4a4a;
            border-bottom: 1px solid #f4f5f6;
            line-height: 1.3;
          }
          tr:last-child td {
            border-bottom: none;
          }
          .status-completed {
            background: #2ba265;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 8px;
            font-weight: 600;
            white-space: nowrap;
          }
          .status-active {
            background: #f59e0b;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 8px;
            font-weight: 600;
            white-space: nowrap;
          }
          .service-tag {
            background: #3b82f6;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 8px;
            font-weight: 600;
            white-space: nowrap;
          }
          .duration-val {
            font-weight: 600;
            color: #1a5f7a;
          }
          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #e5e7eb;
            padding-top: 10px;
          }
          @media print {
            body { margin: 15px; background: white; }
            .flat-card { box-shadow: none; border: 1px solid #e5e7eb; }
            .no-print { display: none; }
            @page { margin: 0.5in; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${isMultipleGuests ? 'All Guests Activity Report' : 'Guest Activity Report'}</h1>
          <p>AAR Guest Management System</p>
          <p style="margin-top: 5px;">Timeframe: ${getTimeframeLabel(reportData.timeframe)} | ${reportData.startDate.toLocaleDateString()} - ${reportData.endDate.toLocaleDateString()}</p>
          ${isMultipleGuests ? `
            <p style="margin-top: 5px;">Total Guests: <strong>${reportData.guests.length}</strong> | Total Check-Ins: <strong>${reportData.totalCheckIns}</strong> | Total Attendance: <strong>${reportData.totalAttendance || reportData.totalCheckIns}</strong></p>
            ${reportData.totalSundayAttendance !== undefined ? `
              <p style="margin-top: 3px; font-size: 11px;">
                Total Sunday Attendance: <strong>${reportData.totalSundayAttendance}</strong>
                (${reportData.sundayCheckIns} checked in + ${reportData.totalAdditionalSunday} additional)
                &nbsp;&bull;&nbsp;
                Total Prayer Meeting Attendance: <strong>${reportData.totalPrayerMeetingAttendance}</strong>
                (${reportData.tuesdayCheckIns} checked in + ${reportData.totalAdditionalTuesday} additional)
              </p>
            ` : ''}
          ` : ''}
        </div>

        ${(reportData.serviceCheckIns && reportData.serviceAdditional) ? (() => {
          const SVC = {
            '9am': 'Sunday 9 AM',
            '12pm': 'Sunday 12 PM',
            '3pm': 'Sunday 3 PM',
            'tuesday_prayer': 'Tuesday Prayer'
          };
          const rows = Object.entries(SVC).map(([sid, label]) => {
            const ci = reportData.serviceCheckIns[sid] || 0;
            const liv = reportData.serviceAdditional[sid]?.['163_livingston'] || 0;
            const sm = reportData.serviceAdditional[sid]?.['smith_lobby'] || 0;
            const total = ci + liv + sm;
            if (total === 0) return '';
            return `<tr>
              <td style="font-weight:600;">${label}</td>
              <td>${ci}</td>
              <td>${liv}</td>
              <td>${sm}</td>
              <td style="font-weight:700;color:#1a5f7a;">${total}</td>
            </tr>`;
          }).filter(Boolean).join('');
          if (!rows) return '';
          const sumLiv = Object.values(reportData.serviceAdditional).reduce((a, x) => a + (x['163_livingston'] || 0), 0);
          const sumSm = Object.values(reportData.serviceAdditional).reduce((a, x) => a + (x['smith_lobby'] || 0), 0);
          return `
            <div style="margin: 0 0 18px 0;">
              <h3 style="margin: 0 0 8px 0; color: #1a5f7a; font-size: 14px;">Service Breakdown</h3>
              <table style="width:100%;border-collapse:collapse;font-size:11px;border:1px solid #eef0f2;">
                <thead>
                  <tr style="background:#f9fafb;">
                    <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:9px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">Service</th>
                    <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:9px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">Checked In</th>
                    <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:9px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">+ 163 Livingston</th>
                    <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:9px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">+ Smith Lobby</th>
                    <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:9px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">Service Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${rows}
                  <tr style="background:#f9fafb;border-top:2px solid #e5e7eb;">
                    <td style="padding:8px 10px;font-weight:700;">Grand Total</td>
                    <td style="padding:8px 10px;font-weight:700;">${reportData.totalCheckIns}</td>
                    <td style="padding:8px 10px;font-weight:700;">${sumLiv}</td>
                    <td style="padding:8px 10px;font-weight:700;">${sumSm}</td>
                    <td style="padding:8px 10px;font-weight:700;color:#1a5f7a;">${reportData.totalAttendance}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          `;
        })() : ''}

        ${reportData.guests.map(guest => {
          const serviceCounts = guest.visits.reduce((acc, v) => {
            const type = formatServiceType(v.serviceType);
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {});
          const lastVisit = guest.visits[guest.visits.length - 1];
          const hasActiveVisit = guest.visits.some(v => v.status !== 'checked_out');
          return `
          <div style="page-break-inside: avoid;">
            <div class="flat-card">
              <div class="card-image">
                ${guest.image
                  ? `<img src="${guest.image}" alt="${guest.name}" />`
                  : `<img src="${placeholderAvatar}" alt="${guest.name}" />`
                }
              </div>
              <div class="card-content">
                <div class="card-top">
                  <span><span class="green-dot">&bull;</span> ${guest.totalVisits} ${guest.totalVisits === 1 ? 'Visit' : 'Visits'} (${reportData.startDate.toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})} - ${reportData.endDate.toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})})</span>
                  <span><span class="green-dot">&bull;</span> ${formatDuration(guest.totalDuration)} Total${guest.totalVisits > 1 ? ` / ${formatDuration(guest.totalDuration / guest.totalVisits)} Avg` : ''} Duration</span>
                </div>
                <div class="card-corner">${Object.entries(serviceCounts).map(([type, count]) => `<span class="service-count-badge">${type}<span class="service-count">${count}</span></span>`).join('')}</div>
                <div class="card-title">${guest.name}</div>
                <div class="card-bottom">
                  ${(guest.email || guest.phone) ? `
                    <div class="bottom-item">
                      <svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                      ${guest.email && guest.phone ? `${guest.email} | ${formatPhoneNumber(guest.phone)}` : (guest.email || formatPhoneNumber(guest.phone))}
                    </div>
                  ` : ''}
                  ${lastVisit ? `
                    <div class="bottom-item">
                      <svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                      ${formatFullDateTime(lastVisit.checkInTime)}${lastVisit.checkOutTime ? ' - ' + formatFullDateTime(lastVisit.checkOutTime) : ''}
                    </div>
                  ` : ''}
                  <div class="bottom-item">
                    <svg viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    ${hasActiveVisit ? 'Active' : 'Completed'}
                  </div>
                </div>
              </div>
            </div>
            <div class="visits-section">
              <h4>Visit History</h4>
              <table>
                <thead>
                  <tr>
                    <th>Check-In</th>
                    <th>Service</th>
                    <th>Check-Out</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Pickup / Vehicle</th>
                  </tr>
                </thead>
                <tbody>
                  ${guest.visits.map(visit => `
                    <tr>
                      <td>${formatFullDateTime(visit.checkInTime)}</td>
                      <td><span class="service-tag">${formatServiceType(visit.serviceType)}</span></td>
                      <td>${visit.checkOutTime ? formatFullDateTime(visit.checkOutTime) : '—'}</td>
                      <td class="duration-val">${visit.duration > 0 ? formatDuration(visit.duration) : '—'}</td>
                      <td><span class="status-${visit.status === 'checked_out' ? 'completed' : 'active'}">${visit.status === 'checked_out' ? 'Completed' : 'Active'}</span></td>
                      <td>
                        <div>${formatPickupVehicle(visit) || '—'}</div>
                        ${visit.pickupNotes ? `<div class="pickup-notes-line">${visit.pickupNotes}</div>` : ''}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        `}).join('')}

        <div class="footer">
          <p>Generated on ${new Date().toLocaleString()}</p>
          <p>AAR Guest Management System - Guest Activity Report</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading reports...</p></div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="reports-title">Guest Activity Reports</h1>
      </div>

      {error && (
        <div className="message error-message">
          {error}
        </div>
      )}

      <div className="reports-content">
        <div className="report-controls">
          <div className="control-group">
            <label htmlFor="guestSearch">Search Guest:</label>
            <div className="search-wrapper">
              <input
                id="guestSearch"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={handleSearchClick}
                placeholder="Click to select a guest..."
                className="guest-search-input"
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              />
              {showDropdown && filteredGuests.length > 0 && (
                <div className="search-dropdown">
                  {filteredGuests.map((guest, index) => (
                    <div
                      key={index}
                      className="dropdown-item"
                      onClick={() => handleGuestSelect(guest)}
                    >
                      <div className="dropdown-guest-image">
                        {guest.image ? (
                          <img src={guest.image} alt={`${guest.firstName} ${guest.lastName}`} />
                        ) : (
                          <img src={placeholderAvatar} alt={`${guest.firstName} ${guest.lastName}`} className="dropdown-guest-avatar-img" />
                        )}
                      </div>
                      <span className="dropdown-guest-name">
                        {guest.firstName} {guest.lastName}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="control-group">
            <label htmlFor="timeframe">Select Timeframe:</label>
            <select
              id="timeframe"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="timeframe-select"
            >
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="quarter">Past Quarter (3 Months)</option>
              <option value="semi-annual">Past 6 Months</option>
              <option value="annual">Past Year (12 Months)</option>
            </select>
          </div>

          {selectedGuest && (
            <div className="button-group">
              <button
                className="btn btn-primary"
                onClick={generateReport}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Generate Report'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={generateAllGuestsReport}
                disabled={generating}
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>

        {reportData && (
          <>
            {reportData.guests.length === 0 ? (
              <div className="report-data">
                <div className="no-data">
                  No guest activity found for the selected timeframe.
                </div>
              </div>
            ) : (
              <div className="report-data">
                {/* Attendance Summary */}
                {reportData.totalSundayAttendance !== undefined && (() => {
                  const activeGuestsCount = allUncheckedGuests.length;
                  const missingPagersCount = reportData.guests.filter(g =>
                    g.visits.some(v =>
                      v.pagerNumber && v.status === 'checked_out' && !v.pagerRetrieved
                    )
                  ).length;
                  const toggle = (key) => setTileFilter(prev => (prev === key ? null : key));
                  const tileProps = (key, titleOff, titleOn) => ({
                    role: 'button',
                    tabIndex: 0,
                    title: tileFilter === key ? titleOn : titleOff,
                    onClick: () => toggle(key),
                    onKeyDown: (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(key);
                      }
                    },
                  });
                  const baseClass = (key) =>
                    `report-attendance-item report-attendance-item-clickable${tileFilter === key ? ' active' : ''}`;
                  return (
                    <div className="report-attendance-summary">
                      <div
                        className={baseClass('total')}
                        {...tileProps('total', 'Click to show all guests', 'Click to clear filter')}
                      >
                        <span className="report-attendance-number">{reportData.totalAttendance}</span>
                        <span className="report-attendance-label">
                          Total Attendance{tileFilter === 'total' ? ' (filter on)' : ''}
                        </span>
                      </div>
                      <div
                        className={baseClass('sunday')}
                        {...tileProps('sunday', 'Click to show only guests with Sunday visits', 'Click to clear filter')}
                      >
                        <span className="report-attendance-number">{reportData.totalSundayAttendance}</span>
                        <span className="report-attendance-label">
                          Sunday ({reportData.sundayCheckIns} checked in + {reportData.totalAdditionalSunday} additional){tileFilter === 'sunday' ? ' (filter on)' : ''}
                        </span>
                      </div>
                      <div
                        className={baseClass('prayer')}
                        {...tileProps('prayer', 'Click to show only guests with Prayer Meeting visits', 'Click to clear filter')}
                      >
                        <span className="report-attendance-number">{reportData.totalPrayerMeetingAttendance}</span>
                        <span className="report-attendance-label">
                          Prayer Meeting ({reportData.tuesdayCheckIns} checked in + {reportData.totalAdditionalTuesday} additional){tileFilter === 'prayer' ? ' (filter on)' : ''}
                        </span>
                      </div>
                      <div
                        className={baseClass('unique')}
                        {...tileProps('unique', 'Click to show all guests', 'Click to clear filter')}
                      >
                        <span className="report-attendance-number">{reportData.totalGuests}</span>
                        <span className="report-attendance-label">
                          Unique Guests{tileFilter === 'unique' ? ' (filter on)' : ''}
                        </span>
                      </div>
                      <div
                        className={`${baseClass('active')}${activeGuestsCount > 0 && tileFilter !== 'active' ? ' underline-sweep-active' : ''}`}
                        {...tileProps('active', 'Click to show all unchecked guests (regardless of period)', 'Click to clear filter')}
                      >
                        <span className="report-attendance-number">{activeGuestsCount}</span>
                        <span className="report-attendance-label">
                          <span className="sweep-text">Unchecked Guests{tileFilter === 'active' ? ' (filter on)' : ''}</span>
                        </span>
                      </div>
                      <div
                        className={`report-attendance-item report-attendance-item-clickable${tileFilter === 'missing' ? ' active-missing' : ''}${missingPagersCount > 0 && tileFilter !== 'missing' ? ' underline-sweep-missing' : ''}`}
                        {...tileProps('missing', 'Click to show only guests with unreturned pagers', 'Click to clear filter')}
                      >
                        <span className="report-attendance-number">{missingPagersCount}</span>
                        <span className="report-attendance-label">
                          <span className="sweep-text-missing">Missing Pagers{tileFilter === 'missing' ? ' (filter on)' : ''}</span>
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {reportData.serviceCheckIns && reportData.serviceAdditional && (() => {
                  const SERVICE_LABELS = {
                    '9am': 'Sunday 9 AM',
                    '12pm': 'Sunday 12 PM',
                    '3pm': 'Sunday 3 PM',
                    'tuesday_prayer': 'Tuesday Prayer'
                  };
                  const LOCATION_LABELS = {
                    '163_livingston': '163 Livingston',
                    'smith_lobby': 'Smith Lobby'
                  };
                  const services = Object.keys(SERVICE_LABELS);
                  const hasAny = services.some((sid) => {
                    const ci = reportData.serviceCheckIns[sid] || 0;
                    const add = (reportData.serviceAdditional[sid]?.['163_livingston'] || 0) +
                                (reportData.serviceAdditional[sid]?.['smith_lobby'] || 0);
                    return ci + add > 0;
                  });
                  if (!hasAny) return null;
                  return (
                    <div className="service-breakdown-section">
                      <h3 className="service-breakdown-title">Service Breakdown</h3>
                      <div className="service-breakdown-table-wrapper">
                        <table className="service-breakdown-table">
                          <thead>
                            <tr>
                              <th>Service</th>
                              <th>Checked In</th>
                              <th>+ {LOCATION_LABELS['163_livingston']}</th>
                              <th>+ {LOCATION_LABELS['smith_lobby']}</th>
                              <th>Service Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {services.map((sid) => {
                              const ci = reportData.serviceCheckIns[sid] || 0;
                              const livingston = reportData.serviceAdditional[sid]?.['163_livingston'] || 0;
                              const smith = reportData.serviceAdditional[sid]?.['smith_lobby'] || 0;
                              const total = ci + livingston + smith;
                              if (total === 0) return null;
                              return (
                                <tr key={sid}>
                                  <td className="sb-service-name">{SERVICE_LABELS[sid]}</td>
                                  <td>{ci}</td>
                                  <td>{livingston}</td>
                                  <td>{smith}</td>
                                  <td className="sb-total">{total}</td>
                                </tr>
                              );
                            })}
                            <tr className="sb-grand-total-row">
                              <td>Grand Total</td>
                              <td>{reportData.totalCheckIns}</td>
                              <td>{services.reduce((a, sid) => a + (reportData.serviceAdditional[sid]?.['163_livingston'] || 0), 0)}</td>
                              <td>{services.reduce((a, sid) => a + (reportData.serviceAdditional[sid]?.['smith_lobby'] || 0), 0)}</td>
                              <td className="sb-total">{reportData.totalAttendance}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                <div className="summary-header">
                  <h2>Guest Activity Report</h2>
                  <div className="export-buttons">
                    <button
                      className="btn btn-export"
                      onClick={exportToCSV}
                    >
                      Export to CSV
                    </button>
                    <button
                      className="btn btn-pdf"
                      onClick={exportToPDF}
                    >
                      Generate PDF
                    </button>
                  </div>
                </div>

                <div className="guest-cards-container">
                  {(tileFilter === 'active'
                    ? allUncheckedGuests.map(g => {
                        const match = guests.find(p => p.firstName === g.firstName && p.lastName === g.lastName);
                        return {
                          ...g,
                          image: match?.image || null,
                          phone: match?.phone || '',
                          email: match?.email || '',
                          emergencyContactName: match?.emergencyContactName || '',
                          emergencyContactPhone: match?.emergencyContactPhone || '',
                        };
                      })
                    : tileFilter === 'missing'
                    ? reportData.guests.filter(g => g.visits.some(v =>
                        v.pagerNumber && v.status === 'checked_out' && !v.pagerRetrieved
                      ))
                    : tileFilter === 'sunday'
                    ? reportData.guests.filter(g => g.visits.some(v => v.serviceType !== 'tuesday_prayer'))
                    : tileFilter === 'prayer'
                    ? reportData.guests.filter(g => g.visits.some(v => v.serviceType === 'tuesday_prayer'))
                    : reportData.guests
                  ).map((guest, index) => {
                    const lastVisit = guest.visits[guest.visits.length - 1];
                    const hasActiveVisit = guest.visits.some(v => v.status !== 'checked_out');
                    const serviceCounts = guest.visits.reduce((acc, v) => {
                      const type = formatServiceType(v.serviceType);
                      acc[type] = (acc[type] || 0) + 1;
                      return acc;
                    }, {});
                    const isExpanded = expandedCards[index];

                    return (
                    <div key={index} className="flat-card-wrapper">
                      <div className="flat-card" onClick={() => toggleCardExpanded(index)}>
                        {/* Left Image Block */}
                        <div className="flat-card-image">
                          {guest.image ? (
                            <img src={guest.image} alt={guest.name} />
                          ) : (
                            <img src={placeholderAvatar} alt={guest.name} />
                          )}
                        </div>

                        {/* Right Content Block */}
                        <div className="flat-card-content">
                          {/* Top Stats Row */}
                          <div className="flat-card-top">
                            <span><span className="green-dot">&bull;</span> {guest.totalVisits} {guest.totalVisits === 1 ? 'Visit' : 'Visits'} ({reportData.startDate.toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})} - {reportData.endDate.toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})})</span>
                            <span><span className="green-dot">&bull;</span> {formatDuration(guest.totalDuration)} Total{guest.totalVisits > 1 ? ` / ${formatDuration(guest.totalDuration / guest.totalVisits)} Avg` : ''} Duration</span>
                          </div>

                          {/* Corner Service Type Badges */}
                          <div className="flat-card-corner">
                            {Object.entries(serviceCounts).map(([type, count]) => (
                              <span key={type} className="service-count-badge">
                                {type}<span className="service-count">{count}</span>
                              </span>
                            ))}
                          </div>

                          {/* Main Title */}
                          <div className="flat-card-title">
                            {guest.name}
                          </div>

                          {/* Bottom Icon Row */}
                          <div className="flat-card-bottom">
                            <div className="flat-card-info-group">
                              {(guest.email || guest.phone) && (
                                <div className="flat-card-info-item">
                                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                  </svg>
                                  {guest.email && guest.phone
                                    ? `${guest.email} | ${formatPhoneNumber(guest.phone)}`
                                    : guest.email || formatPhoneNumber(guest.phone)
                                  }
                                </div>
                              )}

                              {lastVisit && (
                                <div className="flat-card-info-item">
                                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                  </svg>
                                  {formatShortDateTime(lastVisit.checkInTime)}
                                  {lastVisit.checkOutTime ? ` - ${formatShortDateTime(lastVisit.checkOutTime)}` : ''}
                                </div>
                              )}

                              <div className="flat-card-info-item">
                                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                                {hasActiveVisit ? 'Active' : 'Completed'}
                              </div>
                            </div>

                            <div className={`flat-card-expand ${isExpanded ? 'expanded' : ''}`}>
                              <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Visits Section */}
                      {isExpanded && (
                        <div className="flat-card-visits">
                          {isMobile ? (
                            <div className="visits-mobile-grid">
                              {guest.visits.map((visit, vIndex) => (
                                <div key={vIndex} className="visit-card-mobile">
                                  <div className="visit-grid-row">
                                    <div className="visit-grid-cell">
                                      <div className="visit-cell-label">Check-In</div>
                                      <div className="visit-cell-value">{formatFullDateTime(visit.checkInTime)}</div>
                                    </div>
                                    <div className="visit-grid-cell">
                                      <div className="visit-cell-label">Service</div>
                                      <div className="visit-cell-value"><span className="service-tag">{formatServiceType(visit.serviceType)}</span></div>
                                    </div>
                                  </div>
                                  <div className="visit-grid-row">
                                    <div className="visit-grid-cell">
                                      <div className="visit-cell-label">Check-Out</div>
                                      <div className="visit-cell-value">
                                        {visit.checkOutTime ? formatFullDateTime(visit.checkOutTime) : '—'}
                                      </div>
                                    </div>
                                    <div className="visit-grid-cell">
                                      <div className="visit-cell-label">Duration</div>
                                      <div className="visit-cell-value duration-cell">
                                        {visit.duration > 0 ? formatDuration(visit.duration) : '—'}
                                      </div>
                                    </div>
                                  </div>
                                  {(formatPickupVehicle(visit) || visit.pickupNotes) && (
                                    <div className="visit-grid-row">
                                      <div className="visit-grid-cell" style={{ gridColumn: '1 / -1' }}>
                                        <div className="visit-cell-label">Pickup / Vehicle</div>
                                        <div className="visit-cell-value pickup-vehicle-cell">
                                          {formatPickupVehicle(visit) && <div>{formatPickupVehicle(visit)}</div>}
                                          {visit.pickupNotes && (
                                            <div className="pickup-notes-line">{visit.pickupNotes}</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <table className="visits-data-table">
                              <thead>
                                <tr>
                                  <th>Check-In</th>
                                  <th>Service</th>
                                  <th>Check-Out</th>
                                  <th>Duration</th>
                                  <th>Status</th>
                                  <th>Pickup / Vehicle</th>
                                </tr>
                              </thead>
                              <tbody>
                                {guest.visits.map((visit, vIndex) => (
                                  <tr key={vIndex}>
                                    <td>{formatFullDateTime(visit.checkInTime)}</td>
                                    <td><span className="service-tag">{formatServiceType(visit.serviceType)}</span></td>
                                    <td>
                                      {visit.checkOutTime
                                        ? formatFullDateTime(visit.checkOutTime)
                                        : '—'
                                      }
                                    </td>
                                    <td className="duration-cell">
                                      {visit.duration > 0
                                        ? formatDuration(visit.duration)
                                        : '—'
                                      }
                                    </td>
                                    <td>
                                      <span
                                        className={`status-badge ${visit.status} ${visit.id ? 'status-badge-clickable' : ''}`}
                                        role={visit.id ? 'button' : undefined}
                                        tabIndex={visit.id ? 0 : undefined}
                                        title={visit.id ? 'View check-in details' : undefined}
                                        onClick={(e) => {
                                          if (!visit.id) return;
                                          e.stopPropagation();
                                          navigate(`/check-out/${visit.id}`);
                                        }}
                                        onKeyDown={(e) => {
                                          if (!visit.id) return;
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigate(`/check-out/${visit.id}`);
                                          }
                                        }}
                                      >
                                        {visit.status === 'checked_out' ? 'Completed' : 'Active'}
                                      </span>
                                    </td>
                                    <td className="pickup-vehicle-cell">
                                      <div>{formatPickupVehicle(visit) || '—'}</div>
                                      {visit.pickupNotes && (
                                        <div className="pickup-notes-line">{visit.pickupNotes}</div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
